import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle, Loader2, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromoPaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [status, setStatus] = useState("checking"); // checking | paid | not_paid | error
  const [details, setDetails] = useState(null);

  const sessionId = new URLSearchParams(location.search).get("session_id");

  // Avoid setState in an effect for missing session_id
  if (!sessionId && status !== "error") {
    setStatus("error");
  }

  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;

    const poll = async (attempt = 0) => {
      if (cancelled) return;
      if (attempt > 6) {
        setStatus("not_paid");
        return;
      }

      try {
        const resp = await fetch(`${API}/payments/status/${sessionId}`);
        if (!resp.ok) {
          setStatus("error");
          return;
        }
        const data = await resp.json();
        setDetails(data);

        if (data.payment_status === "paid") {
          // Promo campaigns return campaign info after processing
          if (data.promo_campaign_id && data.promo_access_token) {
            navigate(`/promo/campaign/${data.promo_campaign_id}?token=${encodeURIComponent(data.promo_access_token)}`, {
              replace: true,
            });
            return;
          }
          setStatus("paid");
          return;
        }

        if (data.status === "expired") {
          setStatus("not_paid");
          return;
        }

        setTimeout(() => poll(attempt + 1), 1500);
      } catch (e) {
        setStatus("error");
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [navigate, sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0B] relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#D4AF37]/3 rounded-full blur-3xl"></div>
      </div>

      <section className="hero">
        <div className="hero-bg-reflection" aria-hidden>
          <div className="hero-bg-reflection-text">
            <img src="/logo-reflection-trimmed.png" alt="" className="hero-bg-reflection-logo" />
          </div>
        </div>

        <div className="hero-inner">
          <div className="hero-logo">
            <img src="/logo-reflection-trimmed.png" alt="DopeInHD" className="hero-logo-img" />
          </div>
          <p className="hero-subtitle">Confirming your payment…</p>
        </div>
      </section>

      <div className="relative z-10 px-6 pb-16 w-full max-w-2xl mx-auto">
        <Card className="bg-[#121214] border-[#27272A]">
          <CardContent className="pt-6">
            {status === "checking" && (
              <div className="text-center py-6">
                <Loader2 className="w-10 h-10 animate-spin text-[#D4AF37] mx-auto" />
                <p className="text-[#FAFAFA] font-semibold mt-3">Checking payment status</p>
                <p className="text-sm text-[#A1A1AA] mt-1">This may take a few seconds.</p>
              </div>
            )}

            {status === "paid" && (
              <div className="text-center py-6">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto" />
                <p className="text-[#FAFAFA] font-semibold mt-3">Payment confirmed</p>
                <p className="text-sm text-[#A1A1AA] mt-1">Your campaign is being created. If this screen persists, return to the Promo Boost Portal.</p>
                <Button className="btn-gold mt-4" onClick={() => navigate("/promo")}>Return to Promo Boost Portal</Button>
              </div>
            )}

            {status === "not_paid" && (
              <div className="text-center py-6">
                <AlertTriangle className="w-12 h-12 text-[#D4AF37] mx-auto" />
                <p className="text-[#FAFAFA] font-semibold mt-3">Couldn&apos;t confirm payment</p>
                <p className="text-sm text-[#A1A1AA] mt-1">If you completed payment, check your email for confirmation and try again.</p>
                <Button className="btn-gold mt-4" onClick={() => navigate("/promo")}>Back to Promo Boost Portal</Button>
              </div>
            )}

            {status === "error" && (
              <div className="text-center py-6">
                <AlertTriangle className="w-12 h-12 text-red-400 mx-auto" />
                <p className="text-[#FAFAFA] font-semibold mt-3">Error confirming payment</p>
                <p className="text-sm text-[#A1A1AA] mt-1">Please try again.</p>
                <Button className="btn-gold mt-4" onClick={() => navigate("/promo")}>Back to Promo Boost Portal</Button>
              </div>
            )}

            {details && (
              <div className="mt-6 text-xs text-[#71717A]">
                <p>Session: {sessionId}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PromoPaymentSuccess;
